// noinspection JSUnusedGlobalSymbols

import * as Apollo from '@apollo/client';

import { gql } from '@apollo/client';
import type * as Types from '../../../../../graphql/__generated__/types';

const defaultOptions = {} as const;
export type GetDashboardEmployeeQueryVariables = Types.Exact<{ [key: string]: never }>;

export type GetDashboardEmployeeQuery = { __typename?: 'Query' } & {
  currentEmployee: { __typename?: 'CurrentEmployee' } & {
    employee: { __typename?: 'Employee' } & Pick<Types.Employee, 'id' | 'pictureUrl'> & {
        generalInformation: { __typename?: 'EmployeeGeneralInformation' } & Pick<
          Types.EmployeeGeneralInformation,
          'preferredFullName' | 'hiredDate' | 'needsProbationReview'
        >;
        currentMainContract: Types.Maybe<
          { __typename?: 'EmployeeContract' } & Pick<Types.EmployeeContract, 'employeeCode' | 'contractType'> & {
              company: { __typename?: 'Company' } & Pick<Types.Company, 'name'>;
            }
        >;
        currentPosition: Types.Maybe<
          { __typename?: 'EmployeeJobHistory' } & Pick<Types.EmployeeJobHistory, 'level' | 'jobTitle' | 'team'> & {
              businessUnit: { __typename?: 'BusinessUnit' } & Pick<Types.BusinessUnit, 'name'> & {
                  definitions: Array<
                    { __typename?: 'DefinitionItem' } & Pick<Types.DefinitionItem, 'languageCode' | 'content'>
                  >;
                };
              department: { __typename?: 'Department' } & Pick<Types.Department, 'name'> & {
                  definitions: Array<
                    { __typename?: 'DefinitionItem' } & Pick<Types.DefinitionItem, 'languageCode' | 'content'>
                  >;
                };
              job: { __typename?: 'Job' } & Pick<Types.Job, 'name'> & {
                  definitions: Array<
                    { __typename?: 'DefinitionItem' } & Pick<Types.DefinitionItem, 'languageCode' | 'content'>
                  >;
                };
              location: { __typename?: 'Location' } & Pick<Types.Location, 'name'>;
            }
        >;
        currentManagerialAccounting: Types.Maybe<
          { __typename?: 'EmployeeManagerialAccounting' } & {
            vdp: Array<
              { __typename?: 'EpmEmployeeAllocationVdp' } & Pick<Types.EpmEmployeeAllocationVdp, 'sourceType'> & {
                  company: Types.Maybe<{ __typename?: 'Company' } & Pick<Types.Company, 'name'>>;
                } & EpmEmployeeAllocation_EpmEmployeeAllocationVdp_Fragment
            >;
            vdd: Array<
              { __typename?: 'EpmEmployeeAllocationVdd' } & {
                epmMasterVdd: { __typename?: 'EpmMasterVdd' } & Pick<Types.EpmMasterVdd, 'name'>;
              } & EpmEmployeeAllocation_EpmEmployeeAllocationVdd_Fragment
            >;
            functionality: Array<
              { __typename?: 'EpmEmployeeAllocationFunctionality' } & {
                epmMasterFunctionality: { __typename?: 'EpmMasterFunctionality' } & Pick<
                  Types.EpmMasterFunctionality,
                  'name'
                >;
              } & EpmEmployeeAllocation_EpmEmployeeAllocationFunctionality_Fragment
            >;
            roleType: Array<
              { __typename?: 'EpmEmployeeAllocationRoleType' } & {
                epmMasterRoleType: { __typename?: 'EpmMasterRoleType' } & Pick<Types.EpmMasterRoleType, 'name'>;
              } & EpmEmployeeAllocation_EpmEmployeeAllocationRoleType_Fragment
            >;
            allocationKey: Array<
              { __typename?: 'EpmEmployeeAllocationAllocationKey' } & {
                epmMasterAllocationKey: { __typename?: 'EpmMasterAllocationKey' } & Pick<
                  Types.EpmMasterAllocationKey,
                  'name'
                >;
              } & EpmEmployeeAllocation_EpmEmployeeAllocationAllocationKey_Fragment
            >;
          }
        >;
        probationReview: Types.Maybe<{ __typename?: 'ProbationReview' } & DashboardProbationReviewFragment>;
        performanceReviews: Array<{ __typename?: 'PerformanceReview' } & DashboardPerformanceReviewFragment>;
      };
  };
  listDirectReportsProbationReviews: Array<{ __typename?: 'ProbationReview' } & DashboardProbationReviewFragment>;
  listDirectReportsPerformanceReviews: Array<{ __typename?: 'PerformanceReview' } & DashboardPerformanceReviewFragment>;
  employeeIdpDashboardWidget: Array<{ __typename?: 'EmployeeIdp' } & DashboardEmployeeIdpFragment>;
};

export type DashboardProbationReviewFragment = { __typename?: 'ProbationReview' } & Pick<
  Types.ProbationReview,
  'probationStatus' | 'outcome' | 'flags' | 'employeeId' | 'daysOverdue' | 'nextDueDate' | 'probationEndDate'
> & {
    employee: { __typename?: 'Employee' } & {
      generalInformation: { __typename?: 'EmployeeGeneralInformation' } & Pick<
        Types.EmployeeGeneralInformation,
        'preferredFullName'
      >;
    };
    reviews: Array<
      { __typename?: 'ProbationReviewStage' } & Pick<
        Types.ProbationReviewStage,
        'deadline' | 'reviewStatus' | 'isSelfFeedbackFilled' | 'isReviewerFeedbackFilled'
      >
    >;
  };

export type DashboardPerformanceReviewFragment = { __typename?: 'PerformanceReview' } & Pick<
  Types.PerformanceReview,
  'id' | 'companyStage'
> & {
    employee: { __typename?: 'Employee' } & {
      generalInformation: { __typename?: 'EmployeeGeneralInformation' } & Pick<
        Types.EmployeeGeneralInformation,
        'preferredFullName'
      >;
    };
    setting: { __typename?: 'PerformanceReviewSetting' } & Pick<Types.PerformanceReviewSetting, 'stage'>;
    period: { __typename?: 'PerformanceReviewPeriod' } & Pick<
      Types.PerformanceReviewPeriod,
      'year' | 'quarter' | 'isCurrent' | 'version'
    >;
    validation: Array<
      { __typename?: 'PerformanceReviewValidation' } & {
        errors: Array<
          { __typename?: 'PerformanceReviewValidationError' } & Pick<
            Types.PerformanceReviewValidationError,
            'evaluatorType'
          >
        >;
      }
    >;
  };

export type DashboardEmployeeIdpFragment = { __typename?: 'EmployeeIdp' } & Pick<
  Types.EmployeeIdp,
  | 'id'
  | 'employeeId'
  | 'type'
  | 'status'
  | 'startDate'
  | 'endDate'
  | 'reviewDeadlineDate'
  | 'nextReviewDate'
  | 'completedReviewCount'
> & {
    employee: { __typename?: 'Employee' } & {
      generalInformation: { __typename?: 'EmployeeGeneralInformation' } & Pick<
        Types.EmployeeGeneralInformation,
        'preferredFullName'
      >;
    };
  };

export type EpmEmployeeAllocation_EpmEmployeeAllocationAllocationKey_Fragment = {
  __typename?: 'EpmEmployeeAllocationAllocationKey';
} & Pick<Types.EpmEmployeeAllocationAllocationKey, 'id' | 'percentage'>;

export type EpmEmployeeAllocation_EpmEmployeeAllocationFunctionality_Fragment = {
  __typename?: 'EpmEmployeeAllocationFunctionality';
} & Pick<Types.EpmEmployeeAllocationFunctionality, 'id' | 'percentage'>;

export type EpmEmployeeAllocation_EpmEmployeeAllocationRoleType_Fragment = {
  __typename?: 'EpmEmployeeAllocationRoleType';
} & Pick<Types.EpmEmployeeAllocationRoleType, 'id' | 'percentage'>;

export type EpmEmployeeAllocation_EpmEmployeeAllocationVdd_Fragment = {
  __typename?: 'EpmEmployeeAllocationVdd';
} & Pick<Types.EpmEmployeeAllocationVdd, 'id' | 'percentage'>;

export type EpmEmployeeAllocation_EpmEmployeeAllocationVdp_Fragment = {
  __typename?: 'EpmEmployeeAllocationVdp';
} & Pick<Types.EpmEmployeeAllocationVdp, 'id' | 'percentage'>;

export type EpmEmployeeAllocationFragment =
  | EpmEmployeeAllocation_EpmEmployeeAllocationAllocationKey_Fragment
  | EpmEmployeeAllocation_EpmEmployeeAllocationFunctionality_Fragment
  | EpmEmployeeAllocation_EpmEmployeeAllocationRoleType_Fragment
  | EpmEmployeeAllocation_EpmEmployeeAllocationVdd_Fragment
  | EpmEmployeeAllocation_EpmEmployeeAllocationVdp_Fragment;

export const DashboardProbationReviewFragmentDoc = /*#__PURE__*/ gql`
    fragment DashboardProbationReview on ProbationReview {
  probationStatus
  outcome
  flags
  employeeId
  employee {
    generalInformation {
      preferredFullName
    }
  }
  daysOverdue
  nextDueDate
  probationEndDate
  reviews {
    deadline
    reviewStatus
    isSelfFeedbackFilled
    isReviewerFeedbackFilled
  }
}
    `;
export const DashboardPerformanceReviewFragmentDoc = /*#__PURE__*/ gql`
    fragment DashboardPerformanceReview on PerformanceReview {
  id
  employee {
    generalInformation {
      preferredFullName
    }
  }
  setting {
    stage
  }
  period {
    year
    quarter
    isCurrent
    version
  }
  validation(isOnlyNextStage: true) {
    errors {
      evaluatorType
    }
  }
  companyStage
}
    `;
export const DashboardEmployeeIdpFragmentDoc = /*#__PURE__*/ gql`
    fragment DashboardEmployeeIdp on EmployeeIdp {
  id
  employeeId
  type
  status
  startDate
  endDate
  reviewDeadlineDate
  nextReviewDate
  completedReviewCount
  employee {
    generalInformation {
      preferredFullName
    }
  }
}
    `;
export const EpmEmployeeAllocationFragmentDoc = /*#__PURE__*/ gql`
    fragment epmEmployeeAllocation on EpmEmployeeAllocation {
  id
  percentage
}
    `;
export const GetDashboardEmployeeDocument = /*#__PURE__*/ gql`
    query getDashboardEmployee {
  currentEmployee {
    employee {
      id
      pictureUrl
      generalInformation {
        preferredFullName
        hiredDate
        needsProbationReview
      }
      currentMainContract {
        employeeCode
        contractType
        company {
          name
        }
      }
      currentPosition {
        level
        businessUnit {
          name
          definitions {
            languageCode
            content
          }
        }
        department {
          name
          definitions {
            languageCode
            content
          }
        }
        job {
          name
          definitions {
            languageCode
            content
          }
        }
        jobTitle
        level
        location {
          name
        }
        team
      }
      currentManagerialAccounting {
        vdp {
          ...epmEmployeeAllocation
          sourceType
          company {
            name
          }
        }
        vdd {
          ...epmEmployeeAllocation
          epmMasterVdd {
            name
          }
        }
        functionality {
          ...epmEmployeeAllocation
          epmMasterFunctionality {
            name
          }
        }
        roleType {
          ...epmEmployeeAllocation
          epmMasterRoleType {
            name
          }
        }
        allocationKey {
          ...epmEmployeeAllocation
          epmMasterAllocationKey {
            name
          }
        }
      }
      probationReview {
        ...DashboardProbationReview
      }
      performanceReviews(isRecentOnly: true) {
        ...DashboardPerformanceReview
      }
    }
  }
  listDirectReportsProbationReviews {
    ...DashboardProbationReview
  }
  listDirectReportsPerformanceReviews(
    stages: [TARGET_SETTING, TARGET_EVALUATION]
    isRecentOnly: true
  ) {
    ...DashboardPerformanceReview
  }
  employeeIdpDashboardWidget {
    ...DashboardEmployeeIdp
  }
}
    ${EpmEmployeeAllocationFragmentDoc}
${DashboardProbationReviewFragmentDoc}
${DashboardPerformanceReviewFragmentDoc}
${DashboardEmployeeIdpFragmentDoc}`;

/**
 * __useGetDashboardEmployeeQuery__
 *
 * To run a query within a React component, call `useGetDashboardEmployeeQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetDashboardEmployeeQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetDashboardEmployeeQuery({
 *   variables: {
 *   },
 * });
 */
export function useGetDashboardEmployeeQuery(
  baseOptions?: Apollo.QueryHookOptions<GetDashboardEmployeeQuery, GetDashboardEmployeeQueryVariables>,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<GetDashboardEmployeeQuery, GetDashboardEmployeeQueryVariables>(
    GetDashboardEmployeeDocument,
    options,
  );
}
export function useGetDashboardEmployeeLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<GetDashboardEmployeeQuery, GetDashboardEmployeeQueryVariables>,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<GetDashboardEmployeeQuery, GetDashboardEmployeeQueryVariables>(
    GetDashboardEmployeeDocument,
    options,
  );
}
// @ts-expect-error
export function useGetDashboardEmployeeSuspenseQuery(
  baseOptions?: Apollo.SuspenseQueryHookOptions<GetDashboardEmployeeQuery, GetDashboardEmployeeQueryVariables>,
): Apollo.UseSuspenseQueryResult<GetDashboardEmployeeQuery, GetDashboardEmployeeQueryVariables>;
export function useGetDashboardEmployeeSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | Apollo.SuspenseQueryHookOptions<GetDashboardEmployeeQuery, GetDashboardEmployeeQueryVariables>,
): Apollo.UseSuspenseQueryResult<GetDashboardEmployeeQuery | undefined, GetDashboardEmployeeQueryVariables>;
export function useGetDashboardEmployeeSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | Apollo.SuspenseQueryHookOptions<GetDashboardEmployeeQuery, GetDashboardEmployeeQueryVariables>,
) {
  const options = baseOptions === Apollo.skipToken ? baseOptions : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<GetDashboardEmployeeQuery, GetDashboardEmployeeQueryVariables>(
    GetDashboardEmployeeDocument,
    options,
  );
}
export type GetDashboardEmployeeQueryHookResult = ReturnType<typeof useGetDashboardEmployeeQuery>;
export type GetDashboardEmployeeLazyQueryHookResult = ReturnType<typeof useGetDashboardEmployeeLazyQuery>;
export type GetDashboardEmployeeSuspenseQueryHookResult = ReturnType<typeof useGetDashboardEmployeeSuspenseQuery>;
